import { useEffect, useMemo, useRef } from "react";
import { getAccessToken } from "../../../../../api/authService";
import { useUserContext } from "../../../../../contexts/UserContext";
import { getCompanyIdByStoreId } from "../../../helpers/storeHelpers";
import { getENV } from "../../../../../utils";
import { ReactEnvironments } from "../../../../common/constants";

// Only DEV is verified to work. Other environments are untested.
const SYSTEM_NOTIFICATIONS = {
  LOCAL_URL: "http://localhost:3001",
  DEV_URL: "https://app-fixedops-alert-service-ui.app-dev.connectcdk.com",
  DIT_URL: "https://app-fixedops-alert-service-ui.app-stage.connectcdk.com",
  STAGE_URL: "https://app-fixedops-alert-service-ui.app-stage.connectcdk.com",
  PERF_URL: "https://app-fixedops-alert-service-ui.app-perf.connectcdk.com",
  PROD_URL: "https://app-fixedops-alert-service-ui.app.connectcdk.com",
};

const getSystemNotificationsOrigin = (env: string = getENV()): string => {
  switch (env) {
    case ReactEnvironments.Production:
      return SYSTEM_NOTIFICATIONS.PROD_URL;
    case ReactEnvironments.Staging:
      return SYSTEM_NOTIFICATIONS.STAGE_URL;
    case ReactEnvironments.Perf:
      return SYSTEM_NOTIFICATIONS.PERF_URL;
    case ReactEnvironments.DIT:
      return SYSTEM_NOTIFICATIONS.DIT_URL;
    case ReactEnvironments.Develop:
      return SYSTEM_NOTIFICATIONS.DEV_URL;
    case ReactEnvironments.Local:
      return SYSTEM_NOTIFICATIONS.LOCAL_URL; // CHANGE TO SIMULATE DIFFERENT ENV
    default:
      console.warn(
        "[CDCM] System Notifications origin not found. Defaulting to DEV.",
        env,
      );
      return SYSTEM_NOTIFICATIONS.DEV_URL;
  }
};

const SYSTEM_NOTIFICATIONS_PATH: Record<string, string> = {
  SystemNotifications: "alert-configurations",
};

const IFRAME_STYLES = {
  width: "100%",
  height: "100%",
  border: "none",
  minHeight: "80vh",
} as const;

enum ParentMessageType {
  INIT = "CDCM_INIT",
}

enum ChildMessageType {
  READY = "SYSTEM_NOTIFICATIONS_READY",
}

type InitializationPayload = {
  accessToken: string;
  storeId: string | null;
};

type ChildMessage = {
  type: ChildMessageType.READY;
};

const sendInitializationToIframe = (
  iframe: HTMLIFrameElement | null,
  payload: InitializationPayload,
  targetOrigin: string,
): void => {
  iframe?.contentWindow?.postMessage(
    {
      type: ParentMessageType.INIT,
      payload,
    },
    targetOrigin,
  );
};

export default function SystemNotificationsEmbed(): JSX.Element {
  const { selected, iamUserState, defaultStore } = useUserContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const companyId = getCompanyIdByStoreId(
    selected.store.value,
    iamUserState.extendedAccountInfo,
  );

  const targetOrigin = useMemo(() => getSystemNotificationsOrigin(), []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<ChildMessage>) => {
      if (event.origin !== targetOrigin) {
        return;
      }

      if (event.data?.type !== ChildMessageType.READY) {
        return;
      }

      sendInitializationToIframe(
        iframeRef.current,
        {
          accessToken: getAccessToken(),
          storeId: defaultStore.value ?? null,
        },
        targetOrigin,
      );
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [targetOrigin, defaultStore.value]);

  if (!companyId) {
    return <div>Unable to load system notifications setup.</div>;
  }

  const systemNotificationsUrl = `${targetOrigin}/alert-service/${companyId}/${SYSTEM_NOTIFICATIONS_PATH[selected.setup.value]}`;

  return (
    <iframe
      ref={iframeRef}
      src={systemNotificationsUrl}
      title={selected.setup.label}
      style={IFRAME_STYLES}
      data-testid="system-notifications-embed"
    />
  );
}
